import React, { createElement, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiCard } from "@miplatform/components";
import { ATTEMPT_STATUSES } from "@miplatform/constants";
import {
  ButtonVariants,
  Colors,
  IAttemptListItemResponseForWorkOrder,
  IWorkOrder,
  StepType,
  WorkOrderStatus,
} from "@miplatform/types";
import { mdiFileDocumentOutline } from "@mdi/js";
import { EmptyMessageView } from "@mibuildweb/components";
import { useSession } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { WorkOrderEmailStepCard } from "../WorkOrderStepCard/WorkOrderEmailStepCard";
import { WorkOrderStepCard } from "../WorkOrderStepCard/WorkOrderStepCard";

export const WorkOrderDrawerStepsTab = ({
  isAssigneeMode,
  attempt,
  isLoading = false,
  isReadOnly = false,
  workOrder,
  getAttempt,
  onStartClick,
  onReassignClick,
}: WorkOrderDrawerStepsTabProps): React.ReactNode => {
  const { theme, classes } = useStyles();
  const { attemptStatus } = attempt;
  const workOrderName =
    workOrder?.templateName ??
    attempt.assignment?.workOrder?.workOrderTemplateName;
  const startButtonTitle = workOrderName
    ? `Start ${workOrderName}`
    : "Start Work Order";
  const sortedSteps = useMemo(
    () =>
      [...(attempt.steps || [])].sort(
        (a, b) => a.priorityOrder - b.priorityOrder
      ),
    [attempt.steps]
  );
  const { session } = useSession();

  const isStartDisabled =
    [
      WorkOrderStatus.Complete,
      WorkOrderStatus.Cancelled,
      WorkOrderStatus.Hold,
      WorkOrderStatus.New,
      WorkOrderStatus.InProgress,
      WorkOrderStatus.Unassigned,
    ].includes(workOrder?.status as WorkOrderStatus) ||
    attemptStatus === ATTEMPT_STATUSES.CLOSED ||
    isLoading ||
    attempt.assignedUserId !== session.userId;

  const isReassignDisabled =
    [
      WorkOrderStatus.Complete,
      WorkOrderStatus.Cancelled,
      WorkOrderStatus.InProgress,
      WorkOrderStatus.Unassigned,
    ].includes(workOrder?.status as WorkOrderStatus) ||
    attemptStatus === ATTEMPT_STATUSES.CLOSED ||
    isLoading;

  return (
    <Box className={classes.root}>
      {!isReadOnly && (
        <Box className={classes.buttonsContainer}>
          <MiButton
            title={startButtonTitle}
            variant={ButtonVariants.Contained}
            width={isAssigneeMode ? "100%" : "50%"}
            color={Colors.Primary}
            disabled={isStartDisabled}
            onClick={onStartClick}
          />
          {!isAssigneeMode && (
            <MiButton
              title="Reassign"
              variant={ButtonVariants.Outlined}
              width="50%"
              color={Colors.Primary}
              disabled={isReassignDisabled}
              onClick={onReassignClick}
            />
          )}
        </Box>
      )}
      {workOrder?.notes && (
        <MiCard
          leftIcon={mdiFileDocumentOutline}
          title="Notes"
          className={classes.notesContainer}
          titleStyles={{
            fontSize: theme.fontSize.large,
          }}
          containerStyles={{}}
        >
          <Typography fontSize={theme.fontSize.small}>
            {workOrder.notes}
          </Typography>
        </MiCard>
      )}
      <Box className={classes.stepsContainer}>
        {sortedSteps.map((step, index) =>
          createElement(
            step.stepType === StepType.Email
              ? WorkOrderEmailStepCard
              : WorkOrderStepCard,
            {
              key: step.id,
              number: index + 1,
              step,
              attempt,
              isLoading,
              isDisabled: workOrder?.status !== WorkOrderStatus.InProgress,
              onRefresh: getAttempt,
            }
          )
        )}
      </Box>
      {sortedSteps.length === 0 && (
        <EmptyMessageView
          title="No Steps Required"
          subtitle="To complete the work order, click the complete button"
        />
      )}
    </Box>
  );
};

interface WorkOrderDrawerStepsTabProps {
  attempt: IAttemptListItemResponseForWorkOrder;
  isAssigneeMode?: boolean;
  isReadOnly?: boolean;
  workOrder?: IWorkOrder | null;
  isLoading?: boolean;
  getAttempt: () => Promise<void> | void;
  onStartClick?: () => void;
  onReassignClick?: () => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: theme.spacing(1),
    display: "flex",
    flexGrow: 1,
    overflow: "auto",
    flexDirection: "column",
  },
  stepsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  notesContainer: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.light,
    border: `1px solid ${theme.palette.grey.accent}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
}));
